import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, RefreshCw, Link2, ExternalLink, Info } from "lucide-react";
import { api, scanUrl } from "../../api";
import { Badge } from "../../components/ui/badge";
import { RangePicker, KPI, Delta, TrendChart, Breakdown, BarsChart, PrivacyNote } from "./StatsWidgets";

export default function QRStats() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [range, setRange] = useState("7d");
  const [custom, setCustom] = useState(null);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  const params = { range, ...(range === "custom" && custom ? custom : {}) };
  useEffect(() => { (async () => {
    try { const { data } = await api.qrAnalytics(id, params); setData(data); setErr(null); }
    catch (e) { setErr(e?.response?.data?.detail || "Errore di caricamento"); }
  })(); }, [id, range, custom]); // eslint-disable-line

  if (err) return <div className="text-muted-foreground">{err}</div>;
  if (!data) return <div className="text-muted-foreground">Caricamento…</div>;
  const { qr, totals, range: r } = data;

  return (
    <div data-testid="qr-stats-page">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div>
          <button onClick={() => navigate("/app/qr")} className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground mb-2" data-testid="back-to-qr"><ArrowLeft className="h-4 w-4" /> QR Code</button>
          <h1 className="font-display text-2xl md:text-3xl font-extrabold flex items-center gap-2 flex-wrap" data-testid="qr-stats-title">{qr.name}
            {qr.dynamic ? <Badge variant="secondary" className="gap-1 text-[10px]"><RefreshCw className="h-3 w-3" /> Dinamico</Badge> : <Badge variant="outline" className="text-[10px]">Statico</Badge>}
            {qr.status === "paused" && <Badge variant="outline" className="text-[10px] text-amber-600">In pausa</Badge>}
          </h1>
          <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1.5 flex-wrap" data-testid="qr-target">
            <Link2 className="h-3.5 w-3.5" /> Destinazione: <b className="text-foreground">{qr.target_type === "menu" ? "Menù" : qr.target_type === "page" ? "Pagina" : "Link"} · {qr.target_label}</b>
            {qr.dynamic && <a href={scanUrl(qr.code)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline"><ExternalLink className="h-3 w-3" /> apri redirect</a>}
          </p>
        </div>
        <Link to="/app/statistiche" className="text-sm font-semibold text-primary hover:underline" data-testid="go-company-stats">Statistiche azienda →</Link>
      </div>

      {!data.trackable ? (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 text-amber-900 flex gap-3" data-testid="static-notice">
          <Info className="h-5 w-5 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold">Statistiche non disponibili per i QR statici</p>
            <p className="text-sm mt-1 leading-relaxed">Le scansioni non possono essere rilevate perché il QR apre direttamente la destinazione senza passare dai server Qora. Per misurare le scansioni crea un <b>QR dinamico</b>: stesso uso, destinazione modificabile e statistiche complete.</p>
          </div>
        </div>
      ) : (
        <div className="space-y-5">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <KPI label="Scansioni totali" value={totals.all_time} testid="kpi-total" />
            <KPI label="Oggi" value={totals.today} testid="kpi-today" />
            <KPI label="Ultimi 7 giorni" value={totals.last7} testid="kpi-7d" />
            <KPI label="Ultimi 30 giorni" value={totals.last30} testid="kpi-30d" />
          </div>

          <RangePicker value={range} custom={custom} onChange={(k, c) => { setRange(k); if (c) setCustom(c); }} csvUrl={null} />
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <span className="text-muted-foreground">Periodo {r.from} → {r.to}:</span>
            <b data-testid="period-total">{data.period} scansioni</b>
            <Delta pct={data.change_pct} />
            <span className="text-muted-foreground">· <span data-testid="estimated-visitors">{data.estimated_visitors}</span> dispositivi stimati</span>
            <button onClick={() => downloadCsv(api.qrAnalyticsCsvUrl(id, params), `qr-${qr.name}.csv`)} className="ml-auto text-primary font-semibold hover:underline" data-testid="export-csv">Esporta CSV</button>
          </div>

          <TrendChart series={data.series} gran={r.granularity} />

          <div className="grid md:grid-cols-3 gap-3">
            <Breakdown title="Dispositivo" items={data.by_device} testid="breakdown-device" />
            <Breakdown title="Sistema operativo" items={data.by_os} testid="breakdown-os" />
            <Breakdown title="Browser" items={data.by_browser} testid="breakdown-browser" />
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <BarsChart title="Giorno della settimana" items={data.by_weekday} testid="chart-weekday" />
            <BarsChart title="Fascia oraria" items={data.by_hour} testid="chart-hour" />
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <Breakdown title="Provenienza (referrer)" items={data.by_referrer} testid="breakdown-referrer" emptyText="Nessuna provenienza rilevabile: le app fotocamera di solito non la inviano." />
            <Breakdown title="Area geografica (paese)" items={data.by_country} testid="breakdown-country" emptyText="Non disponibile: la rete non fornisce un paese approssimativo per queste scansioni." />
          </div>
          <PrivacyNote />
        </div>
      )}
    </div>
  );
}

export async function downloadCsv(url, filename) {
  const token = localStorage.getItem("qora_token");
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  const blob = await res.blob();
  const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = filename; a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 2000);
}
